<template>
  <div class="admin-menu-view" v-if="visible">
    <div class="menu-view-header">
      <div class="header-left">
        <h2>Menu View</h2>
        <button @click="refreshMenu" class="refresh-btn" title="Refresh menu">
          <img src="@/assets/refresh.svg" alt="Refresh" />
        </button>
      </div>
      <button @click="close" class="close-btn">×</button>
    </div>
    <div class="menu-view-content">
      <iframe
        v-if="menuId"
        :key="iframeKey"
        :src="menuUrl"
        class="menu-iframe"
        frameborder="0"
        @load="onIframeLoad"
      ></iframe>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AdminMenuView',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
      menuId: {
      type: [String, Number],
      default: null
    },
    foodId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      iframeKey: 0
    }
  },
  computed: {
    menuUrl() {
      if (!this.menuId) return ''
      const baseUrl = window.location.origin
      let url = `${baseUrl}/${this.menuId}`
      if (this.foodId) {
        // Use anchor to scroll to item
        url += `#food-${this.foodId}`
      }
      return url
    }
  },
  watch: {
    foodId() {
      // Update iframe URL when foodId changes
      if (this.visible && this.menuId) {
        this.scrollToItem()
      }
    },
    visible(newVal) {
      if (newVal) {
        // Hide header when component becomes visible
        this.$nextTick(() => {
          setTimeout(() => {
            this.hideIframeHeader()
          }, 100)
        })
      }
    },
    menuId() {
      // Hide header when menuId changes (iframe will reload)
      if (this.visible) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.hideIframeHeader()
          }, 100)
        })
      }
    }
  },
  methods: {
    close() {
      this.$emit('close')
    },
    refreshMenu() {
      // Force iframe to reload by incrementing the key
      this.iframeKey += 1
    },
    onIframeLoad() {
      // Hide the header inside the iframe
      this.hideIframeHeader()
      
      // Scroll to item after iframe loads
      if (this.foodId) {
        setTimeout(() => {
          this.scrollToItem()
        }, 1000)
      }
    },
    hideIframeHeader() {
      const iframe = this.$el?.querySelector('.menu-iframe')
      if (!iframe || !iframe.contentWindow) return
      
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document
        if (!iframeDoc) return
        
        // Find and hide the #header element
        const header = iframeDoc.querySelector('#header')
        if (header) {
          header.style.display = 'none'
        }
        
        // Hide the #scrollbar element (with retry logic and MutationObserver)
        const hideScrollbar = () => {
          const scrollbar = iframeDoc.querySelector('#scrollbar')
          if (scrollbar) {
            scrollbar.style.display = 'none'
            return true
          }
          return false
        }
        
        // Try immediately
        if (!hideScrollbar()) {
          // Retry multiple times with increasing delays
          const retries = [100, 300, 500, 1000, 2000]
          retries.forEach(delay => {
            setTimeout(() => {
              hideScrollbar()
            }, delay)
          })
          
          // Use MutationObserver to watch for when the element appears
          const observer = new MutationObserver(() => {
            if (hideScrollbar()) {
              observer.disconnect()
            }
          })
          
          observer.observe(iframeDoc.body || iframeDoc.documentElement, {
            childList: true,
            subtree: true
          })
          
          // Disconnect observer after 5 seconds
          setTimeout(() => {
            observer.disconnect()
          }, 5000)
        }
        
        // Set #editor margin-top to 0
        const setEditorMargin = () => {
          const editor = iframeDoc.querySelector('.editor')
          if (editor) {
            editor.style.marginTop = '0'
            return true
          }
          return false
        }
        
        if (!setEditorMargin()) {
          // Retry for editor as well
          const retries = [100, 300, 500, 1000]
          retries.forEach(delay => {
            setTimeout(() => {
              setEditorMargin()
            }, delay)
          })
        }
      } catch (e) {
        // Cross-origin or other error
        console.warn('Could not modify elements in iframe:', e)
      }
    },
    scrollToItem() {
      if (!this.foodId) return
      
      const iframe = this.$el?.querySelector('.menu-iframe')
      if (!iframe || !iframe.contentWindow) return
      
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document
        if (!iframeDoc) return
        
        // Try multiple selectors to find the food item
        const selectors = [
          `[data-food-id="${this.foodId}"]`,
          `[data-id="${this.foodId}"]`,
          `.item-container[data-id="${this.foodId}"]`,
          `#food-${this.foodId}`,
          `.anchored[data-id="${this.foodId}"]`
        ]
        
        let itemElement = null
        for (const selector of selectors) {
          itemElement = iframeDoc.querySelector(selector)
          if (itemElement) break
        }
        
        if (itemElement) {
          itemElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
          // Highlight the item briefly
          itemElement.style.backgroundColor = '#fff9e6'
          itemElement.style.transition = 'background-color 0.3s'
          setTimeout(() => {
            itemElement.style.backgroundColor = ''
          }, 2000)
        } else {
          // Try using the store's anchored item mechanism
          iframe.contentWindow.$store?.commit('setAnchoredItem', this.foodId)
        }
      } catch (e) {
        // Cross-origin or other error
        console.warn('Could not scroll to item in iframe:', e)
      }
    }
  }
}
</script>

<style scoped>
.admin-menu-view {
  position: fixed;
  right: 0;
  top: 0;
  width: 40%;
  max-width: 600px;
  height: 100vh;
  background-color: #fff;
  z-index: 200000;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.menu-view-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #f9f9f9;
  flex-shrink: 0;
  z-index: 2001;
  position: relative;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.menu-view-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.refresh-btn {
  width: 24px;
  height: 24px;
  background-color: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;
}

.refresh-btn:hover {
  transform: rotate(180deg);
}

.refresh-btn img {
  width: 15px;
  height: 15px;
}

.close-btn {
  width: 30px;
  height: 30px;
  border: 1px solid #ddd;
  background-color: #fff;
  color: #333;
  border-radius: 4px;
  cursor: pointer;
  font-size: 24px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;
  flex-shrink: 0;
  z-index: 2001;
  position: relative;
}

.close-btn:hover {
  background-color: #f5f5f5;
  border-color: #999;
}

.menu-view-content {
  flex: 1;
  overflow: hidden;
  position: relative;
}

.menu-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.loading,
.error {
  padding: 40px;
  text-align: center;
}

.error {
  color: #c33;
}

.highlight-item {
  background-color: #fff9e6 !important;
  transition: background-color 0.3s;
}
</style>

